import React, {
  useCallback, useMemo, useState, useRef, useEffect,
} from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import {
  Typography, Col, Modal, Row, Spin, Button, notification, Progress, message,
} from 'antd';
import { DownloadOutlined } from '@ant-design/icons';
import { DateTime } from 'luxon';
import { Files } from 'ontraccr-common';
import { sortByString } from 'ontraccr-common/lib/Common';
import axios from 'axios';

import {
  downloadFile,
  downloadMultipleFiles,
  formatFileForMetadata,
  getFileDetails,
  getFileType,
  visuallyDownloadFile,
} from '../files/fileHelpers';
import FullPhoto from '../common/photos/FullPhoto';
import { rotateFile } from '../files/state/files.actions';
import DrawerSubmitFooter from '../common/containers/DrawerSubmitFooter';
import PaginatedTable from '../common/containers/PaginatedTable';
import PhotoReelPreview from '../common/photos/PhotoReelPreview';
import { formatTime } from '../helpers/time';
import { getIdMap, sortByNumber, safeExtractFromObject } from '../helpers/helpers';
import OnTraccrButton from '../common/buttons/OnTraccrButton';

const {
  Paragraph,
  Text,
} = Typography;

const DATE_MED_FORMAT = 'LLL d, yyyy';

const NOTIFICATION_KEY = 'attachmentDownloadNotification';

const updateNotification = ({
  notificationMsg,
  percent,
}) => (
  notification.open({
    key: NOTIFICATION_KEY,
    placement: 'bottomRight',
    message: notificationMsg,
    description: (
      <Progress
        type="circle"
        percent={percent}
      />
    ),
  })
);

export default function AttachmentTab({
  attachments: {
    instantiatedFiles,
    locationMap,
  } = {},
  formId,
  loading,
}) {
  const dispatch = useDispatch();
  const settings = useSelector((state) => safeExtractFromObject(state, 'settings.company.settings'));
  const globalFileMap = useSelector((state) => state.files.fileMap);

  const attachments = useMemo(() => {
    if (!instantiatedFiles) return [];
    const sortedFiles = [...instantiatedFiles].sort(
      (a, b) => b.timestamp - a.timestamp,
    );
    return sortedFiles.map((a) => {
      const locationObj = locationMap?.[a.id]?.[0];
      const { location = null, timestamp = null } = locationObj ?? {};
      const finalTimestamp = a?.timestamp ?? timestamp;
      return {
        ...a,
        type: getFileType(a),
        location,
        timestamp: finalTimestamp,
      };
    });
  }, [instantiatedFiles]);

  const [selectedFile, setSelectedFile] = useState({});
  const [showModal, setShowModal] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [isDownloading, setIsDownloading] = useState(false);
  const [currFileIndex, setCurrFileIndex] = useState();
  const [allFilteredData, setAllFilteredData] = useState(attachments);
  const [loadedFileIds, setLoadedFileIds] = useState(new Set());
  const [thumbnailMap, setThumbnailMap] = useState({});

  const attachmentsMap = useMemo(() => getIdMap(attachments), [attachments]);
  const tableDataRef = useRef(attachments);

  const onFileClick = async (file) => {
    if (!file) return;
    const fileSuccess = await visuallyDownloadFile({
      file,
      dispatch,
    });
    if (!fileSuccess) return;

    if (file instanceof File) {
      setSelectedFile({
        file,
      });
    } else {
      let fileDetails = file;
      if (!file.url) {
        fileDetails = {
          ...await getFileDetails(file),
          ...file,
        };
      }

      setSelectedFile({
        ...fileDetails,
      });
    }

    const selectedIndex = tableDataRef.current.findIndex(
      (attachment) => attachment.id === file.id,
    );
    setCurrFileIndex(selectedIndex);
  };

  const clearSelectedFile = useCallback((rotation = -1) => {
    const fileType = Files.getFileType(selectedFile);
    if (fileType === 'image') dispatch(rotateFile(selectedFile?.id, { rotation, formId }, selectedFile?.rotation));
    setSelectedFile({});
  }, [selectedFile, formId]);

  const onNextFileClick = useCallback((isRight) => () => {
    const newIndex = isRight ? currFileIndex + 1 : currFileIndex - 1;
    const isInvalid = isRight ? newIndex >= tableDataRef.current.length : newIndex < 0;

    if (isInvalid) return;

    const nextFile = tableDataRef.current[newIndex];
    if (nextFile) {
      onFileClick(nextFile);
    }
  }, [currFileIndex]);

  const showLeftArrow = useMemo(
    () => selectedFile.id
      && currFileIndex > 0,
    [selectedFile, currFileIndex],
  );
  const showRightArrow = useMemo(
    () => selectedFile.id
      && currFileIndex < (tableDataRef.current.length - 1),
    [selectedFile, currFileIndex],
  );

  const executeDownload = useCallback(() => {
    if (selectedFile?.name) {
      const fullPath = selectedFile.path ? `${selectedFile.path}/${selectedFile.name}` : selectedFile.name;
      downloadFile({
        fileDetails: {
          fullPath,
          name: selectedFile.name,
          VersionId: selectedFile.VersionId,
        },
      });
    } else if (selectedFile?.url) {
      downloadFile({
        fileURL: selectedFile.url,
        saveName: selectedFile.name,
      });
    }
    if (showModal) setShowModal(false);
  }, [selectedFile]);

  const handleOnDownload = useCallback(() => {
    const { isExternal } = selectedFile;

    if (isExternal) {
      setShowModal(true);
      return;
    }
    executeDownload();
  }, [selectedFile, executeDownload]);

  const onDownload = useCallback(async () => {
    const filesToDownload = selectedFiles.map((fileId) => {
      const file = attachmentsMap[fileId];
      const { name, path } = file;
      return {
        fullPath: `${path}/${name}`,
        name,
      };
    });

    await downloadMultipleFiles({
      filesToDownload,
      setDownloadingFiles: setIsDownloading,
      updateNotification,
      notificationKey: NOTIFICATION_KEY,
    });
  }, [attachmentsMap, selectedFiles]);

  const handleFileSelection = useCallback((selectedRowKeys) => {
    setSelectedFiles(selectedRowKeys);
  }, []);

  const onSelectAll = useCallback((checked) => {
    const allIds = allFilteredData.map((file) => file.id);
    setSelectedFiles(checked ? allIds : []);
  }, [allFilteredData]);

  const handleFilteredDataChanged = useCallback((filteredData) => {
    tableDataRef.current = filteredData;
    setAllFilteredData(filteredData);
  }, []);

  const onPageDataChanged = useCallback(async (pageData) => {
    const toFetch = pageData.filter((file) => (
      !loadedFileIds.has(file.id) && !thumbnailMap[file.id]
    ));
    if (!toFetch.length) return;
    try {
      const toFetchFileIds = toFetch.map((file) => file.id);
      const { data } = await axios.get('/files/thumbnails', {
        params: {
          fileIds: toFetchFileIds,
        },
      });
      const newMap = {
        ...thumbnailMap,
        ...data,
      };
      const notInMap = toFetchFileIds.filter((fileId) => !newMap[fileId]);
      if (notInMap.length) {
        setLoadedFileIds((prev) => {
          const newSet = new Set(prev);
          notInMap.forEach((fileId) => newSet.add(fileId));
          return newSet;
        });
      }
      setThumbnailMap(newMap);
    } catch (error) {
      message.error('Failed to get image thumbnails');
    }
  }, [loadedFileIds, thumbnailMap]);

  const onImageLoad = useCallback((fileId) => {
    setLoadedFileIds((prev) => {
      const newSet = new Set(prev);
      newSet.add(fileId);
      return newSet;
    });
  }, []);

  // Clean up selected files when filtered data changes
  useEffect(() => {
    if (selectedFiles.length) {
      const filteredIdSet = new Set(allFilteredData.map((file) => file.id));
      const newSelected = selectedFiles.filter((id) => filteredIdSet.has(id));
      if (newSelected.length !== selectedFiles.length) {
        setSelectedFiles(newSelected);
      }
    }
  }, [allFilteredData, selectedFiles]);

  // Update allFilteredData when attachments change
  useEffect(() => {
    setAllFilteredData(attachments);
  }, [attachments]);

  const tableColumns = useMemo(() => {
    const fileTypeFilters = Array.from(
      new Set(attachments.map((file) => file.type)),
    ).sort()
      .map((type) => ({
        text: type,
        value: type,
      }));

    const locationFilters = Array.from(
      new Set(Object.values(locationMap ?? {}).map((value) => value[0]?.location)),
    ).sort()
      .map((location) => ({
        text: location,
        value: location,
      }));

    const onLocationFilter = (value, record) => (
      record.location === value
    );

    const rawDates = Array.from(
      new Set(
        attachments.map((file) => (
          file.timestamp
            ? DateTime.fromMillis(file.timestamp).toLocaleString(DateTime.DATE_MED)
            : 'N/A'
        )),
      ),
    );
    const sortedDates = rawDates
      .sort((a, b) => {
        if (a === 'N/A') return 1; // Put N/A option at the end
        if (b === 'N/A') return -1;
        const dtA = DateTime.fromFormat(a, DATE_MED_FORMAT);
        const dtB = DateTime.fromFormat(b, DATE_MED_FORMAT);
        return dtA.toMillis() - dtB.toMillis(); // Ascending order
      });
    const uploadDateFilters = sortedDates.map((date) => ({
      text: date,
      value: date,
    }));

    const onUploadDateFilter = (value, file) => {
      if (!file.timestamp && value === 'N/A') return true;
      if (!file.timestamp) return false;
      const date = formatTime(file.timestamp, DateTime.DATE_MED);
      return date === value;
    };

    return [
      {
        title: 'Type',
        dataIndex: 'type',
        key: 'type',
        filters: fileTypeFilters,
        onFilter: (value, record) => record.type === value,
        width: '100px',
        render: (type, file) => (
          <PhotoReelPreview
            file={file}
            type={type}
            small
            showCondensedView
            shadow={false}
            locationMap={locationMap}
            onImageLoad={onImageLoad}
            thumbnailMap={thumbnailMap}
          />
        ),
      },
      {
        title: 'Name',
        dataIndex: 'name',
        key: 'name',
        sorter: sortByString('name'),
        width: '50%',
        render: (name, file) => (
          <Button
            type="link"
            onClick={() => onFileClick(file)}
            style={{ textWrap: 'wrap', textAlign: 'left' }}
          >
            {name}
          </Button>
        ),
      },
      {
        title: 'Location',
        dataIndex: 'location',
        key: 'location',
        sorter: sortByString('location'),
        filters: locationFilters,
        onFilter: onLocationFilter,
      },
      {
        title: 'Upload Date',
        dataIndex: 'timestamp',
        key: 'uploadDate',
        sorter: sortByNumber('timestamp', true),
        defaultSortOrder: 'descend',
        filters: uploadDateFilters,
        onFilter: onUploadDateFilter,
        render: (timestamp) => {
          if (!timestamp) return <Text>N/A</Text>;
          const date = formatTime(timestamp, DateTime.DATE_MED);
          return <Text>{date}</Text>;
        },
      },
    ];
  }, [attachments, locationMap, thumbnailMap, onImageLoad]);

  const onHeaderRow = useCallback(() => {
    if (!settings?.uxRedesignPt1) return null;
    return {
      className: 'ontraccr-table-header',
    };
  }, [settings]);

  return (
    <>
      {
        loading
          ? (
            <Row style={{ marginTop: '40vh', width: '100%' }} justify="center" align="middle">
              <Col>
                <Spin />
              </Col>
            </Row>
          ) : (
            <Row justify="end">
              <Row align="middle" gutter={24} style={{ marginBottom: 10 }}>
                <Col>
                  <span>
                    Selected <b>{selectedFiles.length}</b> item{selectedFiles.length !== 1 ? 's' : ''}
                  </span>
                </Col>
                <Col>
                  <OnTraccrButton
                    type="cancel"
                    title="Download"
                    icon={<DownloadOutlined />}
                    onClick={onDownload}
                    disabled={!selectedFiles.length || selectedFiles.length > 100}
                    style={{ pointerEvents: 'auto' }}
                    loading={isDownloading}
                  />
                </Col>
              </Row>
              <PaginatedTable
                dataSource={attachments}
                columns={tableColumns}
                scrollOffset={291}
                containerStyle={{ height: 'calc(100vh - 270px)' }}
                size="small"
                onHeaderRow={onHeaderRow}
                onFilteredDataChanged={handleFilteredDataChanged}
                rowSelection={{
                  selectedRowKeys: selectedFiles,
                  onChange: handleFileSelection,
                  onSelectAll,
                  getCheckboxProps: () => ({
                    disabled: false,
                    className: settings?.uxRedesignPt1 ? 'ontraccr-checkbox' : '',
                  }),
                }}
                rowKey="id"
                onPageDataChanged={onPageDataChanged}
              />
            </Row>
          )
      }
      <FullPhoto
        file={globalFileMap[selectedFile.id]}
        url={selectedFile.url}
        type={selectedFile.type}
        rotation={selectedFile.rotation}
        onClose={clearSelectedFile}
        onDownload={handleOnDownload}
        onLeft={onNextFileClick(false)}
        onRight={onNextFileClick(true)}
        showLeft={showLeftArrow}
        showRight={showRightArrow}
        showMetadata
        path={formatFileForMetadata({ fileMap: attachmentsMap, selectedFile })}
      />

      <Modal
        type="warning"
        visible={showModal}
        title={(
          <div style={{ textAlign: 'left' }}>
            <Text style={{ fontSize: '0.8em' }}>
              Download External File
            </Text>
          </div>
        )}
        bodyStyle={{ borderRadius: 0, paddingBottom: 30, backgroundColor: '#ffffff' }}
        onCancel={() => setShowModal(false)}
        maskClosable
        footer={(
          <DrawerSubmitFooter
            onSubmit={executeDownload}
            onClose={() => setShowModal(false)}
            submitTitle="Download"
          />
        )}
      >
        <Col style={{
          margin: 15,
          textAlign: 'left',
        }}
        >
          <Paragraph style={{ fontSize: '0.6em' }}>
            This file was uploaded by an external user from a public form.
            It is recommended to scan the file for viruses before opening it.
            Ontraccr is not responsible for the content of this file.
          </Paragraph>
        </Col>
      </Modal>
    </>
  );
}

/* eslint-disable react/forbid-prop-types */
AttachmentTab.propTypes = {
  attachments: PropTypes.shape({
    instantiatedFiles: PropTypes.array,
    locationMap: PropTypes.object,
  }),
  formId: PropTypes.string,
  loading: PropTypes.bool,
};

AttachmentTab.defaultProps = {
  attachments: {
    instantiatedFiles: [],
    locationMap: {},
  },
  formId: null,
  loading: false,
};
